/**
 * Organization Sign-up Page
 *
 * Combined sign-up flow for organization owners.
 * Creates both the owner account AND the organization in one flow.
 * After setup, redirects to organization settings to invite team members.
 */

import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from './auth-context'
import * as orgApi from './api/organizations'
import './OrgSignup.css'

// Plan details for display
const PLAN_DETAILS = {
  standard: {
    name: 'Standard',
    price: 250,
    period: 'month',
    members: 5,
    surveys: 10,
  },
  business: {
    name: 'Business',
    price: 1000,
    period: 'month',
    members: 20,
    surveys: 50,
  },
  enterprise: {
    name: 'Enterprise',
    price: null,
    period: 'custom',
    members: 'Unlimited',
    surveys: 'Unlimited',
  },
}

function OrgSignup() {
  const { user, loading, register, refreshOrganizations } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const selectedPlan = searchParams.get('plan')

  const [step, setStep] = useState(1) // 1 = Owner details, 2 = Org details
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  // Owner (account) details
  const [ownerData, setOwnerData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  })

  // Organization details
  const [orgData, setOrgData] = useState({
    name: '',
    description: '',
    contactEmail: '',
    contactPhone: '',
  })

  // If user is already logged in, skip to org setup
  useEffect(() => {
    if (!loading && user) {
      // User already logged in, skip to step 2 or redirect to org-setup
      if (selectedPlan) {
        navigate(`/org-setup?plan=${selectedPlan}`, { replace: true })
      } else {
        navigate('/pricing', { replace: true })
      }
    }
  }, [user, loading, selectedPlan, navigate])

  // Redirect if no valid plan selected
  if (!selectedPlan || !PLAN_DETAILS[selectedPlan]) {
    return (
      <div className="org-signup-page">
        <div className="org-signup-container">
          <div className="org-signup-header">
            <h1>Select a Plan First</h1>
            <p>Please choose a pricing plan to continue with organization setup.</p>
          </div>
          <div className="form-actions" style={{ justifyContent: 'center' }}>
            <Link to="/pricing" className="btn-primary">
              View Pricing Plans
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const planInfo = PLAN_DETAILS[selectedPlan]

  const handleOwnerChange = (e) => {
    const { name, value } = e.target
    setOwnerData((prev) => ({ ...prev, [name]: value }))
    setError('')
  }

  const handleOrgChange = (e) => {
    const { name, value } = e.target
    setOrgData((prev) => ({ ...prev, [name]: value }))
    setError('')
  }

  const validateStep1 = () => {
    if (!ownerData.fullName.trim()) {
      setError('Full name is required')
      return false
    }
    if (!ownerData.email.trim()) {
      setError('Email is required')
      return false
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerData.email)) {
      setError('Please enter a valid email address')
      return false
    }
    if (!ownerData.password) {
      setError('Password is required')
      return false
    }
    if (ownerData.password.length < 8) {
      setError('Password must be at least 8 characters')
      return false
    }
    if (ownerData.password !== ownerData.confirmPassword) {
      setError('Passwords do not match')
      return false
    }
    return true
  }

  const validateStep2 = () => {
    if (!orgData.name.trim()) {
      setError('Organization name is required')
      return false
    }
    if (orgData.name.trim().length < 3) {
      setError('Organization name must be at least 3 characters')
      return false
    }
    if (!orgData.contactEmail.trim()) {
      setError('Contact email is required')
      return false
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(orgData.contactEmail)) {
      setError('Please enter a valid contact email')
      return false
    }
    return true
  }

  const handleNextStep = () => {
    if (validateStep1()) {
      setStep(2)
      setError('')
    }
  }

  const handlePrevStep = () => {
    setStep(1)
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateStep2()) {
      return
    }

    try {
      setSubmitting(true)
      setError('')

      // Step 1: Register the owner account
      await register(ownerData.email, ownerData.password, ownerData.fullName)

      // Step 2: Create the organization
      const slug =
        orgData.name
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '') + `-${Date.now().toString(36)}`

      await orgApi.createOrganization({
        name: orgData.name.trim(),
        slug,
        description: orgData.description.trim() || null,
        subscriptionPlanCode: selectedPlan,
        // Store contact details in organization settings
        settings: {
          contactEmail: orgData.contactEmail.trim(),
          contactPhone: orgData.contactPhone.trim() || null,
        },
      })

      // Refresh organizations and wait a moment for state to propagate
      await refreshOrganizations()

      // Small delay to ensure React state updates have propagated
      await new Promise((resolve) => setTimeout(resolve, 100))

      // Redirect to organization settings so owner can invite team members
      navigate('/organization?welcome=true')
    } catch (err) {
      console.error('[OrgSignup] Failed to create organization:', err)
      setError(err.message || 'Failed to create organization. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="org-signup-page">
        <div className="org-signup-loading">
          <div className="loading-spinner">
            <div className="spinner-ring"></div>
          </div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="org-signup-page">
      <div className="org-signup-container">
        <div className="org-signup-header">
          <Link to="/" className="signup-logo">
            <span className="logo-text">SurveyTip</span>
          </Link>
          <h1>Create Your Organization</h1>
          <p>Set up your {planInfo.name} plan account</p>
        </div>

        {/* Plan Summary Card */}
        <div className="plan-summary-card">
          <div className="plan-summary-header">
            <span className="plan-badge">{planInfo.name}</span>
            <div className="plan-price">
              {planInfo.price !== null ? (
                <>
                  <span className="currency">₹</span>
                  <span className="amount">{planInfo.price}</span>
                  <span className="period">/{planInfo.period}</span>
                </>
              ) : (
                <span className="custom-price">Custom Pricing</span>
              )}
            </div>
          </div>
          <div className="plan-summary-features">
            <div className="feature-item">
              <span className="feature-icon">👥</span>
              <span>{planInfo.members} team members</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📋</span>
              <span>{planInfo.surveys} surveys</span>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="signup-steps">
          <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <span className="step-number">{step > 1 ? '✓' : '1'}</span>
            <span className="step-label">Owner Account</span>
          </div>
          <div className="step-connector"></div>
          <div className={`step ${step >= 2 ? 'active' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Organization Details</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="org-signup-form">
          {error && <div className="form-error">{error}</div>}

          {/* Step 1: Owner Account */}
          {step === 1 && (
            <div className="form-step">
              <h2 className="step-title">Owner Account Details</h2>
              <p className="step-subtitle">
                You'll be the owner and administrator of this organization
              </p>

              <div className="form-group">
                <label htmlFor="fullName">Full Name *</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={ownerData.fullName}
                  onChange={handleOwnerChange}
                  placeholder="Enter your full name"
                  required
                  autoFocus
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={ownerData.email}
                  onChange={handleOwnerChange}
                  placeholder="you@company.com"
                  required
                  disabled={submitting}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">Password *</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={ownerData.password}
                    onChange={handleOwnerChange}
                    placeholder="Min. 8 characters"
                    required
                    minLength={8}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password *</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={ownerData.confirmPassword}
                    onChange={handleOwnerChange}
                    placeholder="Confirm password"
                    required
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-actions">
                <Link to="/pricing" className="btn-secondary">
                  Back to Pricing
                </Link>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleNextStep}
                  disabled={submitting}>
                  Next: Organization Details →
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Organization Details */}
          {step === 2 && (
            <div className="form-step">
              <h2 className="step-title">Organization Details</h2>
              <p className="step-subtitle">Tell us about your organization</p>

              <div className="form-group">
                <label htmlFor="name">Organization Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={orgData.name}
                  onChange={handleOrgChange}
                  placeholder="Your company or team name"
                  required
                  autoFocus
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description (Optional)</label>
                <textarea
                  id="description"
                  name="description"
                  value={orgData.description}
                  onChange={handleOrgChange}
                  placeholder="Brief description of your organization"
                  rows={3}
                  disabled={submitting}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contactEmail">Contact Email *</label>
                  <input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    value={orgData.contactEmail}
                    onChange={handleOrgChange}
                    placeholder="contact@company.com"
                    required
                    disabled={submitting}
                  />
                  <span className="form-hint">For billing and important notifications</span>
                </div>
                <div className="form-group">
                  <label htmlFor="contactPhone">Phone Number (Optional)</label>
                  <input
                    type="tel"
                    id="contactPhone"
                    name="contactPhone"
                    value={orgData.contactPhone}
                    onChange={handleOrgChange}
                    placeholder="+91 98765 43210"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handlePrevStep}
                  disabled={submitting}>
                  ← Back
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting || !orgData.name.trim() || !orgData.contactEmail.trim()}>
                  {submitting ? 'Creating Organization...' : 'Create Organization'}
                </button>
              </div>
            </div>
          )}
        </form>

        <div className="signup-footer">
          <p>
            Already have an account? <Link to="/login">Sign in here</Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default OrgSignup
